// Minimal WebGL2 fragment-shader canvas.
//
// Wraps a single full-screen quad + user-supplied fragment shader.
// Built-in uniforms always available:
//   uniform float u_time;        // seconds since start()
//   uniform vec2  u_resolution;  // canvas size in CSS pixels
//   uniform float u_pixelRatio;  // devicePixelRatio (clamped to 2)
//   uniform vec2  u_mouse;       // -1..1, smoothed; (0,0) until set
// User uniforms can be passed in `uniforms` map and updated via setUniforms().
// Supported: number (float), [a,b] (vec2), [a,b,c] (vec3), [a,b,c,d] (vec4).
//
// Lifecycle: start() kicks the rAF loop; stop() pauses; dispose() releases
// GL resources (program, buffers, VAO) and removes the canvas. Visibility
// gating + context-loss recovery are caller responsibilities — the canvas
// just exposes the controls.

const VERT_SRC = `#version 300 es
in vec2 a_position;
out vec2 v_uv;
void main() {
  v_uv = a_position * 0.5 + 0.5;
  gl_Position = vec4(a_position, 0.0, 1.0);
}
`;

type UniformValue = number | number[] | Float32Array;

export interface ShaderCanvasOptions {
  uniforms?: Record<string, UniformValue>;
  pixelRatioCap?: number;
  /** When true, render only when start() is called and the loop is running. */
  manual?: boolean;
}

export class ShaderCanvas {
  readonly canvas: HTMLCanvasElement;
  private host: HTMLElement;
  private fragmentSrc: string;
  private gl: WebGL2RenderingContext | null = null;
  private program: WebGLProgram | null = null;
  private vao: WebGLVertexArrayObject | null = null;
  private buffer: WebGLBuffer | null = null;
  private uniformLocs = new Map<string, WebGLUniformLocation | null>();
  private uniforms: Record<string, UniformValue> = {};
  private startTime = 0;
  private rafId: number | null = null;
  private resizeObs: ResizeObserver | null = null;
  private pixelRatioCap: number;
  private mouse = { x: 0, y: 0 };

  constructor(host: HTMLElement, fragmentSrc: string, opts: ShaderCanvasOptions = {}) {
    this.host = host;
    this.fragmentSrc = fragmentSrc;
    this.pixelRatioCap = opts.pixelRatioCap ?? 2;
    this.uniforms = { ...(opts.uniforms ?? {}) };

    const canvas = document.createElement('canvas');
    canvas.style.position = 'absolute';
    canvas.style.inset = '0';
    canvas.style.width = '100%';
    canvas.style.height = '100%';
    canvas.style.display = 'block';
    canvas.style.pointerEvents = 'none';
    this.canvas = canvas;
    this.host.appendChild(canvas);
  }

  /** Update one or more uniform values. Unknown uniforms are silently ignored. */
  setUniforms(updates: Record<string, UniformValue>): void {
    for (const [k, v] of Object.entries(updates)) {
      this.uniforms[k] = v;
    }
  }

  /** Smoothed mouse — caller updates target each pointermove; we lerp toward it. */
  setMouse(x: number, y: number): void {
    this.mouse.x = x;
    this.mouse.y = y;
  }

  /** Begin (or resume) the render loop. Lazily compiles on first call. */
  start(): void {
    if (!this.gl) {
      if (!this.init()) return;
    }
    if (this.rafId !== null) return;
    this.startTime = performance.now() / 1000;
    const loop = () => {
      this.render();
      this.rafId = requestAnimationFrame(loop);
    };
    this.rafId = requestAnimationFrame(loop);
  }

  /** Pause the render loop without releasing GL resources. */
  stop(): void {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  /** Release all GL resources and remove the canvas. */
  dispose(): void {
    this.stop();
    this.resizeObs?.disconnect();
    this.resizeObs = null;
    if (this.gl) {
      const gl = this.gl;
      if (this.program) gl.deleteProgram(this.program);
      if (this.vao) gl.deleteVertexArray(this.vao);
      if (this.buffer) gl.deleteBuffer(this.buffer);
      const ext = gl.getExtension('WEBGL_lose_context');
      ext?.loseContext();
    }
    this.gl = null;
    this.program = null;
    this.vao = null;
    this.buffer = null;
    this.uniformLocs.clear();
    this.canvas.remove();
  }

  // ----- internals --------------------------------------------------------

  private init(): boolean {
    const gl = this.canvas.getContext('webgl2', {
      antialias: false,
      premultipliedAlpha: true,
      preserveDrawingBuffer: false,
      powerPreference: 'low-power',
    });
    if (!gl) return false;
    this.gl = gl;

    const vs = this.compile(gl.VERTEX_SHADER, VERT_SRC);
    const fs = this.compile(gl.FRAGMENT_SHADER, this.fragmentSrc);
    if (!vs || !fs) return false;

    const program = gl.createProgram();
    if (!program) return false;
    gl.attachShader(program, vs);
    gl.attachShader(program, fs);
    gl.bindAttribLocation(program, 0, 'a_position');
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      console.warn('[ShaderCanvas] link failed:', gl.getProgramInfoLog(program));
      gl.deleteProgram(program);
      gl.deleteShader(vs);
      gl.deleteShader(fs);
      return false;
    }
    gl.deleteShader(vs);
    gl.deleteShader(fs);
    this.program = program;

    const vao = gl.createVertexArray();
    const buffer = gl.createBuffer();
    if (!vao || !buffer) return false;
    gl.bindVertexArray(vao);
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-1, -1, 3, -1, -1, 3]),
      gl.STATIC_DRAW
    );
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
    gl.bindVertexArray(null);
    this.vao = vao;
    this.buffer = buffer;

    this.resizeObs = new ResizeObserver(() => this.resize());
    this.resizeObs.observe(this.canvas);
    this.resize();

    return true;
  }

  private compile(type: number, src: string): WebGLShader | null {
    const gl = this.gl!;
    const sh = gl.createShader(type);
    if (!sh) return null;
    gl.shaderSource(sh, src);
    gl.compileShader(sh);
    if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
      console.warn('[ShaderCanvas] compile failed:', gl.getShaderInfoLog(sh), '\n', src);
      gl.deleteShader(sh);
      return null;
    }
    return sh;
  }

  private resize(): void {
    const gl = this.gl;
    if (!gl) return;
    const dpr = Math.min(window.devicePixelRatio || 1, this.pixelRatioCap);
    const rect = this.canvas.getBoundingClientRect();
    const w = Math.max(1, Math.floor(rect.width * dpr));
    const h = Math.max(1, Math.floor(rect.height * dpr));
    if (this.canvas.width !== w) this.canvas.width = w;
    if (this.canvas.height !== h) this.canvas.height = h;
    gl.viewport(0, 0, w, h);
  }

  private getLoc(name: string): WebGLUniformLocation | null {
    if (this.uniformLocs.has(name)) return this.uniformLocs.get(name)!;
    const gl = this.gl!;
    const loc = gl.getUniformLocation(this.program!, name);
    this.uniformLocs.set(name, loc);
    return loc;
  }

  private setUniform(name: string, v: UniformValue): void {
    const gl = this.gl!;
    const loc = this.getLoc(name);
    if (!loc) return;
    if (typeof v === 'number') {
      gl.uniform1f(loc, v);
    } else if (v.length === 2) {
      gl.uniform2f(loc, v[0], v[1]);
    } else if (v.length === 3) {
      gl.uniform3f(loc, v[0], v[1], v[2]);
    } else if (v.length === 4) {
      gl.uniform4f(loc, v[0], v[1], v[2], v[3]);
    }
  }

  private render(): void {
    const gl = this.gl;
    if (!gl || !this.program) return;
    gl.useProgram(this.program);
    gl.bindVertexArray(this.vao);

    // Built-in uniforms.
    const t = performance.now() / 1000 - this.startTime;
    this.setUniform('u_time', t);
    this.setUniform('u_resolution', [this.canvas.width, this.canvas.height]);
    this.setUniform('u_pixelRatio', Math.min(window.devicePixelRatio || 1, this.pixelRatioCap));
    this.setUniform('u_mouse', [this.mouse.x, this.mouse.y]);

    // User uniforms (overrides any same-named built-ins).
    for (const [k, v] of Object.entries(this.uniforms)) {
      this.setUniform(k, v);
    }

    gl.drawArrays(gl.TRIANGLES, 0, 3);
    gl.bindVertexArray(null);
  }
}
